import React, { useState, useContext, useEffect } from 'react';
import { 
  signInWithEmailAndPassword, 
  createUserWithEmailAndPassword,
  signInWithPopup,
  GoogleAuthProvider,
  sendPasswordResetEmail,
  updateProfile
} from 'firebase/auth';
import { auth } from '../../Firebase/firebase';
import { AuthContext } from '../ProviderAuth/AuthProvider';
import { useNavigate } from 'react-router-dom';
import swal from 'sweetalert';

const Login = () => {
  const [authState, dispatch] = useContext(AuthContext);
  const navigate = useNavigate();
  
  // Estados del formulario
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    nombre: ''
  });
  const [isLogin, setIsLogin] = useState(true);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);

  // Redirigir si ya está autenticado
  useEffect(() => {
    if (authState?.user?.uid && authState?.estadoUsuario === 'activo') {
      navigate('/prestamos/informacion');
    }
  }, [authState, navigate]);

  // Validaciones
  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validatePassword = (password) => {
    return password.length >= 6;
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.email) {
      newErrors.email = 'El email es requerido';
    } else if (!validateEmail(formData.email)) {
      newErrors.email = 'Email inválido';
    }

    if (!formData.password) {
      newErrors.password = 'La contraseña es requerida';
    } else if (!validatePassword(formData.password)) {
      newErrors.password = 'La contraseña debe tener al menos 6 caracteres';
    }

    if (!isLogin) {
      if (!formData.nombre) {
        newErrors.nombre = 'El nombre es requerido';
      }
      if (formData.password !== formData.confirmPassword) {
        newErrors.confirmPassword = 'Las contraseñas no coinciden';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Manejo de errores de Firebase
  const handleFirebaseError = (error) => {
    console.log(error);
    switch (error.code) {
      case 'auth/user-not-found':
        return 'Usuario no encontrado';
      case 'auth/wrong-password':
        return 'Contraseña incorrecta';
      case 'auth/email-already-in-use':
        return 'Este email ya está registrado';
      case 'auth/weak-password':
        return 'La contraseña es demasiado débil';
      case 'auth/invalid-email':
        return 'Email inválido';
      case 'auth/too-many-requests':
        return 'Demasiados intentos fallidos. Inténtalo más tarde';
      case 'auth/network-request-failed':
        return 'Error de conexión. Verifica tu internet';
      default:
        return 'Error de autenticación. Inténtalo de nuevo';
    }
  };

  // Inicio de sesión con email y contraseña
  const handleEmailLogin = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    setLoading(true);
    try {
      await signInWithEmailAndPassword(
        auth, 
        formData.email.trim().toLowerCase(), 
        formData.password
      );
      
      swal({
        title: '¡Bienvenido!',
        text: 'Inicio de sesión exitoso',
        icon: 'success',
        timer: 2000
      });
      
    } catch (error) {
      swal({
        title: 'Error',
        text: handleFirebaseError(error),
        icon: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  // Registro con email y contraseña
  const handleEmailRegister = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    setLoading(true);
    try {
      const userCredential = await createUserWithEmailAndPassword(
        auth, 
        formData.email.trim().toLowerCase(), 
        formData.password
      );

      // Actualizar perfil con nombre
      await updateProfile(userCredential.user, {
        displayName: formData.nombre
      });
      
      swal({
        title: '¡Registro exitoso!',
        text: 'Tu cuenta ha sido creada correctamente',
        icon: 'success',
        timer: 2000
      });
      
    } catch (error) {
      swal({
        title: 'Error en el registro',
        text: handleFirebaseError(error),
        icon: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  // Inicio de sesión con Google
  const handleGoogleLogin = async () => {
    setLoading(true);
    try {
      const provider = new GoogleAuthProvider();
      provider.setCustomParameters({
        prompt: 'select_account'
      });

      await signInWithPopup(auth, provider);
      
      swal({
        title: '¡Bienvenido!',
        text: 'Inicio de sesión con Google exitoso',
        icon: 'success',
        timer: 2000
      });
      
    } catch (error) {
      if (error.code !== 'auth/popup-closed-by-user') {
        swal({
          title: 'Error',
          text: handleFirebaseError(error),
          icon: 'error'
        });
      }
    } finally {
      setLoading(false);
    }
  };

  // Recuperar contraseña
  const handlePasswordReset = async () => {
    if (!formData.email) {
      swal({
        title: 'Email requerido',
        text: 'Por favor ingresa tu email para recuperar la contraseña',
        icon: 'warning'
      });
      return;
    }

    if (!validateEmail(formData.email)) {
      swal({
        title: 'Email inválido',
        text: 'Por favor ingresa un email válido',
        icon: 'warning'
      });
      return;
    }

    try {
      await sendPasswordResetEmail(auth, formData.email);
      swal({
        title: 'Email enviado',
        text: 'Revisa tu correo para restablecer tu contraseña',
        icon: 'success'
      });
    } catch (error) {
      swal({
        title: 'Error',
        text: handleFirebaseError(error),
        icon: 'error'
      });
    }
  };

  // Manejo de cambios en el formulario
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Limpiar error del campo cuando el usuario empiece a escribir
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  return (
    <div className='w-full flex-col h-screen justify-center items-center flex from-gray-900 via-gray-900 bg-gradient-to-t to-sky-900'>
      <div className='flex flex-col items-center h-full'>
        <div className='flex-grow flex items-center'>
          <div className='border px-6 py-8 border-gray-200 shadow-2xl rounded-lg flex flex-col items-center text-white bg-gray-800 min-w-[400px]'>
            
            {/* Header */}
            <div className='text-center mb-6'>
              <h2 className='text-2xl font-bold mb-2'>
                {isLogin ? 'Iniciar Sesión' : 'Crear Cuenta'}
              </h2>
              <p className='text-gray-300 text-sm'>
                {isLogin ? 'Ingresa a tu cuenta' : 'Crea tu nueva cuenta'}
              </p>
            </div>

            {/* Formulario */}
            <form onSubmit={isLogin ? handleEmailLogin : handleEmailRegister} className='w-full space-y-4'>
              
              {/* Campo Nombre (solo para registro) */}
              {!isLogin && (
                <div>
                  <label className='block text-sm font-medium mb-1'>Nombre completo</label>
                  <input
                    type="text"
                    name="nombre"
                    value={formData.nombre}
                    onChange={handleInputChange}
                    className={`w-full px-3 py-2 bg-gray-700 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      errors.nombre ? 'border-red-500' : 'border-gray-600'
                    }`}
                    placeholder="Tu nombre completo"
                    disabled={loading}
                  />
                  {errors.nombre && <p className='text-red-400 text-xs mt-1'>{errors.nombre}</p>}
                </div>
              )}

              {/* Campo Email */}
              <div>
                <label className='block text-sm font-medium mb-1'>Email</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  className={`w-full px-3 py-2 bg-gray-700 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    errors.email ? 'border-red-500' : 'border-gray-600'
                  }`}
                  placeholder="tu@email.com"
                  disabled={loading}
                />
                {errors.email && <p className='text-red-400 text-xs mt-1'>{errors.email}</p>}
              </div>

              {/* Campo Contraseña */}
              <div>
                <label className='block text-sm font-medium mb-1'>Contraseña</label>
                <div className='relative'>
                  <input
                    type={showPassword ? "text" : "password"}
                    name="password"
                    value={formData.password}
                    onChange={handleInputChange}
                    className={`w-full px-3 py-2 bg-gray-700 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 pr-10 ${
                      errors.password ? 'border-red-500' : 'border-gray-600'
                    }`}
                    placeholder="Mínimo 6 caracteres"
                    disabled={loading}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className='absolute right-3 top-2.5 text-gray-400 hover:text-white'
                    disabled={loading}
                  >
                    {showPassword ? '👁️' : '👁️‍🗨️'}
                  </button>
                </div>
                {errors.password && <p className='text-red-400 text-xs mt-1'>{errors.password}</p>}
              </div>

              {/* Campo Confirmar Contraseña (solo para registro) */}
              {!isLogin && (
                <div>
                  <label className='block text-sm font-medium mb-1'>Confirmar contraseña</label>
                  <input
                    type={showPassword ? "text" : "password"}
                    name="confirmPassword"
                    value={formData.confirmPassword}
                    onChange={handleInputChange}
                    className={`w-full px-3 py-2 bg-gray-700 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      errors.confirmPassword ? 'border-red-500' : 'border-gray-600'
                    }`}
                    placeholder="Confirma tu contraseña"
                    disabled={loading}
                  />
                  {errors.confirmPassword && <p className='text-red-400 text-xs mt-1'>{errors.confirmPassword}</p>}
                </div>
              )}

              {/* Botón de envío */}
              <button
                type="submit"
                disabled={loading}
                className='w-full bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white font-medium py-2 px-4 rounded-md transition duration-200 flex items-center justify-center'
              >
                {loading ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Procesando...
                  </>
                ) : (
                  isLogin ? 'Iniciar Sesión' : 'Crear Cuenta'
                )}
              </button>
            </form>

            {/* Separador */}
            <div className='flex items-center w-full my-6'>
              <hr className='flex-1 border-gray-600' />
              <span className='px-3 text-gray-400 text-sm'>o</span>
              <hr className='flex-1 border-gray-600' />
            </div>

            {/* Botón Google */}
            <button
              onClick={handleGoogleLogin}
              disabled={loading}
              className='w-full bg-red-600 hover:bg-red-700 disabled:bg-red-400 text-white font-medium py-2 px-4 rounded-md transition duration-200 flex items-center justify-center'
            >
              <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              Continuar con Google
            </button>

            {/* Enlaces adicionales */}
            <div className='flex flex-col items-center space-y-2 mt-6'>
              {isLogin && (
                <button
                  type="button"
                  onClick={handlePasswordReset}
                  className='text-blue-400 hover:text-blue-300 text-sm'
                  disabled={loading}
                >
                  ¿Olvidaste tu contraseña?
                </button>
              )}
              
              <button
                type="button"
                onClick={() => {
                  setIsLogin(!isLogin);
                  setFormData({ email: '', password: '', confirmPassword: '', nombre: '' });
                  setErrors({});
                }}
                className='text-gray-300 hover:text-white text-sm'
                disabled={loading}
              >
                {isLogin ? '¿No tienes cuenta? Regístrate' : '¿Ya tienes cuenta? Inicia sesión'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;